/**
 * CollapsibleSection — Reusable progressive disclosure component.
 */

import React, { useState } from 'react';
import { ChevronRight, type LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

interface CollapsibleSectionProps {
  title: string;
  icon: LucideIcon;
  badge?: string;
  badgeVariant?: 'default' | 'info' | 'success' | 'warning';
  defaultOpen?: boolean;
  visible?: boolean;
  description?: string;
  children: React.ReactNode;
}

const badgeColors = {
  default: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400',
  info: 'bg-primary/10 text-primary dark:bg-primary/10 dark:text-primary',
  success: 'bg-green-50 text-green-600 dark:bg-green-900/30 dark:text-green-400',
  warning: 'bg-amber-50 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400',
};

export function CollapsibleSection({
  title,
  icon: Icon,
  badge,
  badgeVariant = 'default',
  defaultOpen = false,
  visible = true,
  description,
  children,
}: CollapsibleSectionProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  if (!visible) return null;

  return (
    <div className="border-b border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900 last:border-b-0">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          'flex w-full items-center gap-3 px-4 py-3 text-left transition-colors',
          'hover:bg-slate-50 dark:hover:bg-slate-800',
          isOpen && 'bg-slate-50 dark:bg-slate-800/60',
        )}
      >
        <div className={cn(
          'flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg transition-colors',
          isOpen
            ? 'bg-primary/10 text-primary dark:bg-primary/20 dark:text-primary'
            : 'bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400',
        )}>
          <Icon className="h-4 w-4" />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-slate-700 dark:text-slate-200">
              {title}
            </span>
            {badge && (
              <span className={cn(
                'inline-flex items-center rounded-md px-1.5 py-0.5 text-[10px] font-medium',
                badgeColors[badgeVariant],
              )}>
                {badge}
              </span>
            )}
          </div>
          {description && (
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 truncate">
              {description}
            </p>
          )}
        </div>

        <ChevronRight className={cn(
          'h-4 w-4 flex-shrink-0 text-slate-400 dark:text-slate-500 transition-transform duration-200',
          isOpen && 'rotate-90',
        )} />
      </button>

      {isOpen && (
        <div className="px-4 pb-5 pt-2">
          {children}
        </div>
      )}
    </div>
  );
}

export function SectionGroupHeader({ title }: { title: string }) {
  return (
    <div className="px-4 py-2 bg-slate-100 dark:bg-slate-800 border-y border-slate-200 dark:border-slate-700">
      <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
        {title}
      </span>
    </div>
  );
}
